'use strict';

import React from 'react';
import PropTypes from 'prop-types';

import { useMealOperations, usePermissions, useModalControls } from '../contexts';
import AddToCollectionButton from '@components/Recipes/AddToCollectionButton.react';
import { getConfig } from '@utils/Env';
import confirm from '@utils/Confirm';

import './MealEditControls.scss';

const MealEditControls = ({
    meal,
    primary,
    content,
    sortedMeals,
    canShowRemove = true
}) => {
    const { removeMeal } = useMealOperations();
    const { canWrite, canCreate, isPro, user } = usePermissions();
    const { editRecipe, editFood } = useModalControls();

    const handleEditMeal = () => {
        if (!content) return;

        if (content.type === 'recipe') {
            editRecipe(content, meal);
        } else if (content.type === 'food') {
            editFood(content, meal);
        }
    };

    const handleRemoveMeal = async () => {
        if (await confirm('Are you sure you want to remove this meal?')) {
            removeMeal(meal.uuid || meal.id);
        }
    };

    // Don't show controls if user can't edit (allow virtual plans if user can create)
    if (!canWrite && !canCreate) {
        return null;
    }

    // Don't show controls if no content to edit
    if (!content) {
        return null;
    }

    const showRemoveButton = canShowRemove && sortedMeals?.length > 0;
    const isCommercialFood = ['Restaurant Dish', 'Ready Made Meal', 'Meal Kit'].includes(content.product_type);

    return (
        <div className="meal-edit-controls">
            {/* Admin controls for foods */}
            {user?.role === 'admin' && meal.food_uuid && (
                <a
                    target="_blank"
                    rel="noreferrer"
                    href={`https://${getConfig('www_host')}/admin/foods/${meal.food_uuid}`}
                    title="Admin food editor"
                >
                    <button className="btn btn-sm btn-outline-secondary">
                        <i className="feather feather-box"></i>
                        <span>Admin</span>
                    </button>
                </a>
            )}

            {/* Admin controls for recipes */}
            {user?.role === 'admin' && meal.recipe_uuid && (
                <a
                    target="_blank"
                    rel="noreferrer"
                    href={`https://${getConfig('www_host')}/publisher/recipe/${meal.recipe_uuid}`}
                    title="Admin recipe editor"
                >
                    <button className="btn btn-sm btn-outline-secondary">
                        <i className="feather feather-box"></i>
                        <span>Admin</span>
                    </button>
                </a>
            )}

            {/* Add to Collection for recipes */}
            {content.type === 'recipe' && (
                <AddToCollectionButton recipe={content} />
            )}

            {!isCommercialFood && (
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleEditMeal}
                    title={`Edit ${content.type}`}
                >
                    <i className="feather feather-edit"></i>
                    <span>Edit</span>
                </button>
            )}

            {showRemoveButton && (
                <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemoveMeal}
                    title="Remove this meal"
                >
                    <i className="feather feather-trash"></i>
                    <span>Remove</span>
                </button>
            )}
        </div>
    );
};

MealEditControls.propTypes = {
    meal: PropTypes.object.isRequired,
    primary: PropTypes.object,
    content: PropTypes.object.isRequired,
    sortedMeals: PropTypes.array,
    canShowRemove: PropTypes.bool,
};

export default MealEditControls;
